/** MSME-friendly labels for readiness checks and orchestration UI. */

export const READINESS_CHECK_PRIMARY_LABEL = 'Check my reporting readiness';
export const READINESS_CHECK_PRIMARY_SUBTITLE =
  'Scans your bills, disclosure fields, and buyer requests · usually about 1 minute';

export const READINESS_ACTION_LABELS = {
  updateActionList: 'Update action list',
  updateActionListHint: 'Refreshes the prioritized gap list without changing your data.',
  autoFixFromDocuments: 'Auto-fix from my bills & documents',
  autoFixHint: 'Matches uploaded bills to transactions and closes checkpoints where possible.',
  fullReadinessCheck: 'Run full readiness check',
  fullReadinessCheckHint: 'Same as the main button — checks BRSR, ISO, and export modules.',
  orchestrateAllReports: 'Generate statutory & export reports',
  orchestrateAllReportsHint:
    'Builds BRSR, ISO, CBAM, certification packs (ZED/EUDR/ESRS/export), and compliance hub packs in lightweight stages (readiness check is separate).',
  orchestrateAllReportsFull: 'Full orchestration (readiness + all reports)',
  orchestrateAllReportsFullHint:
    'Runs readiness workflows then every applicable report bundle including certification standards, PAT, green finance, and more.',
  analyzeEmissionsForReports: 'Analyze emissions for reports',
  analyzeEmissionsForReportsHint:
    'Runs the emissions agent pipeline, then BRSR / ISO / CBAM / certification reporting agents to refresh framework outputs.',
  reportingAutomationTitle: 'Agentic reporting automation',
  reportingAutomationSubtitle:
    'Multi-agent workflows refresh readiness scores, framework reports, certification packs, and export bundles from your live data.',
} as const;

export const MODULE_CHECK_LABELS = {
  brsr: {
    button: 'Check BRSR readiness',
    running: 'Checking BRSR…',
    subtitle: 'Reviews mandatory India disclosure fields and lists what is still missing.',
    panelTitle: 'BRSR readiness check',
  },
  iso: {
    button: 'Check ISO assurance readiness',
    running: 'Checking ISO…',
    subtitle: 'Reviews inventory boundary, evidence, and assurance checkpoints.',
    panelTitle: 'ISO 14064 readiness check',
  },
  cbam: {
    button: 'Check EU export (CBAM) readiness',
    running: 'Checking CBAM…',
    subtitle: 'Verifies installation boundary, energy bills, and export documentation.',
    panelTitle: 'CBAM readiness check',
  },
  pcf: {
    button: 'Analyze feed & calculate PCF',
    running: 'Analyzing feed data…',
    subtitle: 'AI analyzes transactions and workflow data, attributes products, and calculates ISO 14067 footprints.',
    panelTitle: 'Product footprint analysis',
  },
} as const;

export const ORCHESTRATOR_MSME_LABELS: Record<string, string> = {
  reporting_readiness: 'Full reporting readiness check',
  iso_assurance: 'ISO 14064 readiness check',
  brsr_disclosure: 'BRSR readiness check',
  product_pcf: 'Product footprint analysis',
  product_pcf_analysis: 'Product PCF feed analysis',
  cbam_readiness: 'EU export (CBAM) check',
  certification_standards: 'Multi-standard certification & reporting pack',
  third_party_verifier: 'Third-party verifier handoff',
  activity_data_uplift: 'Activity data quality uplift',
  full_report_generation: 'Full report generation',
  emissions_reporting: 'Emissions & reporting analysis',
};

export const FRAMEWORK_ACRONYM_HINTS: Record<string, string> = {
  BRSR: 'Business Responsibility & Sustainability Report — India ESG disclosure for listed companies and value-chain suppliers.',
  ISO14064: 'International standard for organizational greenhouse gas inventories and verification.',
  ISO14067: 'International standard for product carbon footprint (PCF) calculations.',
  CBAM: 'EU Carbon Border Adjustment Mechanism — carbon reporting for goods exported to the European Union.',
  ZED: 'MSME Sustainable (ZED) — Zero Defect Zero Effect certification for Indian manufacturers.',
  EUDR: 'EU Deforestation Regulation — due diligence and geolocation for agri/forest commodities.',
  ESRS: 'European Sustainability Reporting Standards — environmental topics E1–E5 for CSRD value chains.',
  CDP: 'Carbon Disclosure Project — common buyer climate questionnaire.',
  CSRD: 'Corporate Sustainability Reporting Directive — EU rules that flow down to suppliers.',
  GRI: 'Global Reporting Initiative — emissions and sustainability disclosure standards.',
  TCFD: 'Task Force on Climate-related Financial Disclosures / ISSB climate risks.',
  ECOVADIS: 'EcoVadis sustainability rating readiness for supplier scorecards.',
  SUPPLIER_PORTAL: 'Buyer audit requests and supplier questionnaires registered in your hub.',
  ADVISORY: 'AI advisory suggestion based on your current data.',
};

export const STAGE_MSME_LABELS: Record<string, string> = {
  baseline_assessment: 'Reviewed your current readiness scores',
  baseline_inventory: 'Loaded your emissions inventory',
  snapshot_assessment: 'Loaded your BRSR disclosure snapshot',
  inventory_quality_review: 'Checked bill and transaction data quality',
  inventory_quality_advisor: 'Checked bill and transaction data quality',
  document_evidence_verification: 'Matched uploaded bills to activities',
  buyer_pack_advisory: 'Reviewed buyer questionnaire gaps',
  buyer_request_advisor: 'Reviewed buyer questionnaire gaps',
  gap_synthesis: 'Summarized open gaps across modules',
  principle6_disclosure_review: 'Checked BRSR Principle 6 disclosure fields',
  scope_reconciliation: 'Reconciled Scope 1, 2, and 3 totals',
  environmental_kpi_review: 'Reviewed energy and environmental KPIs',
  readiness_synthesis: 'Calculated updated BRSR readiness score',
  organizational_boundary: 'Confirmed organizational boundary',
  operational_boundary: 'Confirmed operational boundary',
  boundary_enforcement: 'Checked boundary rules against transactions',
  iso14064_compliance_review: 'Ran ISO 14064 compliance checks',
  iso_evidence_collector: 'Collected evidence for assurance',
  iso_gap_closure_planner: 'Built gap-closure action plan',
  assurance_gate: 'Checked assurance readiness gate',
  readiness_assessment: 'Calculated updated readiness score',
  brsr_disclosure_orchestration: 'Checked BRSR disclosure readiness',
  iso_assurance_orchestration: 'Checked ISO assurance readiness',
  product_pcf_orchestration: 'Checked product footprint readiness',
  cbam_readiness_orchestration: 'Checked EU export (CBAM) readiness',
  certification_standards_orchestration: 'Checked ZED, EUDR, ESRS, and export-pack certification readiness',
  readiness_improvements_apply: 'Applied ISO, BRSR, PCF, CBAM, and buyer-pack improvements',
  readiness_autopilot: 'Auto-fixed gaps from bills and documents',
  activity_data_uplift_orchestration: 'Raised activity-based data quality',
  third_party_verifier_orchestration: 'Prepared third-party verifier handoff',
  spend_proxy_reduction_advisor: 'Checked spend-based vs metered activity data',
  action_prioritization: 'Built your prioritized action list',
  exporter_precheck: 'Confirmed export/CBAM applicability',
  installation_emissions_derivation: 'Derived installation emissions from bills',
  installation_boundary_verifier: 'Verified factory location (GPS/site photo)',
  documentation_verifier: 'Verified CBAM documentation checklist',
  assurance_uplift: 'Updated assurance checkpoints',
  hub_improvements_applied: 'Saved improvements to your compliance hub',
  workflow_orchestration: 'Ran reporting readiness workflows',
  statutory_reports: 'Generated statutory report bundles',
  supplier_export_reports: 'Built India and export compliance hub packs',
  certification_pack: 'Built multi-standard certification & auditor guidance pack',
  finance_pat_hydrogen_reports: 'Generated PAT, green H₂, and finance reports',
  bundle_assembly: 'Assembled downloadable report bundle index',
};

export const humanizeOrchestrationStage = (stageKey = ''): string => {
  const normalized = String(stageKey).trim();
  if (!normalized) {
    return 'Processing';
  }
  if (STAGE_MSME_LABELS[normalized]) {
    return STAGE_MSME_LABELS[normalized];
  }
  return normalized.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

export const buildStageOutcomeBullets = (
  stages: Array<{ stage: string; status: string }> = []
): string[] => {
  const bullets: string[] = [];
  stages.forEach((entry) => {
    if (entry.status === 'completed') {
      bullets.push(humanizeOrchestrationStage(entry.stage));
    }
  });
  if (stages.some((entry) => entry.status === 'failed')) {
    bullets.push('Some checks need your review — see the action list below.');
  }
  return bullets.slice(-5);
};

export const EMPTY_READINESS_CHECK_PROMPT =
  'You have not run a readiness check yet. Upload at least 2–3 utility or fuel bills, then tap Check my reporting readiness.';

export const ORCHESTRATION_ONBOARDING_TIP = {
  title: 'One button checks India and export readiness',
  subtitle:
    'Tap the readiness button once — we scan bills, BRSR fields, ISO boundaries, and CBAM export checks together.',
  steps: [
    'Check my reporting readiness',
    'Open gap tabs for the standard you need',
    'Auto-fix from bills when prompted',
  ],
  dismiss: 'Got it',
} as const;

export const REPORTING_HUB_HANDOFF =
  'Follow the steps below to raise your score. Upload bills first, then run a readiness check.';
